<template>
    <div class="image-box">
        <Transition name="fade" mode="out-in" :duration="150">
            <div class="wrapper" @click.stop="dismiss" v-if="visible" :class="{ zoomed: zoomed }">
                <button class="close-btn" @click.stop="dismiss">
                    <FeatherIcon name="x" />
                </button>

                <button @click.stop="next(-1)" v-if="images.length > 1">
                    <FeatherIcon name="chevron-left" />
                </button>

                <div class="image-container">
                    <button class="remove" @click.stop="handleRemove" v-if="onRemove">
                        <FeatherIcon name="trash-2" />
                    </button>
                    <img :key="images[current]" :src="images[current]" alt="" @click.stop="zoom" ref="currentImage" :class="{ 'can-zoom': true }" />
                </div>

                <button @click.stop="next(1)" v-if="images.length > 1">
                    <FeatherIcon name="chevron-right" />
                </button>
            </div>
        </Transition>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, Ref, watch, onMounted, onUnmounted } from 'vue'
import { useImageBox } from '.'

const zoomed: Ref<boolean> = ref(false)
const currentImage = ref(null)

const { imageBox } = useImageBox()
const { images, visible, dismiss, current, onRemove } = imageBox

const zoom = () => {
    if (!currentImage.value || !currentImage.value.naturalHeight) return

    if (!zoomed.value && currentImage.value.naturalHeight > currentImage.value.height) {
        zoomed.value = !zoomed.value
        return
    }
    if (zoomed.value) {
        zoomed.value = false
        return
    }
}
const canZoom = computed(() => {
    if (!currentImage.value || !currentImage.value.naturalHeight) {
        return false
    }
    return currentImage.value.naturalHeight > currentImage.value.height
})

const next = (value: number) => {
    current.value += value
    if (current.value > images.value.length - 1) {
        current.value = 0
    }
    if (current.value < 0) {
        current.value = images.value.length - 1
    }
}

const handleRemove = async () => {
    if (!onRemove.value) return
    const indexToRemove = current.value
    const confirmed = await onRemove.value(indexToRemove)
    if (confirmed === false) return
    images.value.splice(indexToRemove, 1)
    if (images.value.length === 0) {
        dismiss()
        return
    }
    if (current.value >= images.value.length) {
        current.value = images.value.length - 1
    }
}

const handleKeydown = (e: KeyboardEvent) => {
    if (!visible.value || images.value.length <= 1) return
    if (e.key === 'ArrowRight') {
        next(1)
    } else if (e.key === 'ArrowLeft') {
        next(-1)
    }
}

onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
})

watch(images, () => {
    zoomed.value = false
})
</script>

<style lang="scss" scoped>
.image-box {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1000;
}

.wrapper {
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.45);
    overflow: scroll;
    padding: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    button {
        border: 0;
        background: #fff;
        font-size: 2rem;
        color: var(--c-color-primary);
        width: 60px;
        height: 60px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        flex-shrink: 0;
        transition: transform 0.2s ease;

        &:active {
            transform: scale(0.96);
        }
    }

    button.close-btn {
        position: absolute;
        top: 20px;
        right: 20px;
        background: transparent;
        color: #fff;
        box-shadow: none;
        z-index: 20;
    }

    .image-container {
        position: relative;
        display: inline-block;
        margin: 0 auto;
        max-width: 90%;

        img {
            display: block;
            max-height: 95vh;
            max-width: 100%;
            box-shadow: 0 0 30px rgba(0, 0, 0, 0.45);

            &.can-zoom {
                cursor: zoom-in;
            }
        }

        .remove {
            position: absolute;
            top: 15px;
            right: 15px;
            width: 54px;
            height: 54px;
            background: rgba(255, 255, 255, 0.9);
            color: var(--c-color-alert);
            font-size: 1.5rem;
            z-index: 10;
            transition:
                background 0.2s ease,
                color 0.2s ease,
                transform 0.2s ease;

            &:hover {
                background: var(--c-color-alert);
                color: #fff;
                transform: scale(1.08);
            }

            &:active {
                transform: scale(0.96);
            }
        }
    }

    &.zoomed {
        padding: 0px 10px;
        display: block;

        .image-container {
            max-width: none;
            display: block;

            img {
                max-height: none;
                max-width: 100%;
                cursor: zoom-out;
                margin: 0 auto;
            }
        }

        button {
            display: none;
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
